import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Loader2,
  Plug,
  Sparkles,
  Store,
  Terminal,
  XCircle,
} from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { Card, CardBody } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Field, Input, Textarea } from "../components/ui/Input";
import { StatusBadge } from "../components/ui/Badge";
import { ConnectorPicker } from "../components/connector/ConnectorPicker";
import { useAuth } from "../lib/auth";
import { ApiError, createFromChat, installListing, isJobDone, waitForJob } from "../lib/api";
import type { ExchangeListing, GenerationJob } from "../lib/api";
import { cn } from "../lib/cn";

const EXAMPLES = [
  "Connect our Salesforce sandbox over OAuth2 and add a capability to query recent opportunities.",
  "A connector for a REST API with a health check and a list_users capability using a bearer token. Mock httpx in tests.",
  "Read rows from our Postgres orders table with a run_query capability.",
];

/** The visual pipeline. Each step lights up once the job's own progress
 *  passes its threshold — these are the server's real phases, not a timer. */
const STEPS: { label: string; at: number }[] = [
  { label: "Understanding your request", at: 0 },
  { label: "Drafting the connector with Wise 2.0 AI", at: 10 },
  { label: "Running tests in the gVisor sandbox", at: 55 },
  { label: "Signing config & registering", at: 85 },
];

/** Survives a refresh or a tab switch: generation runs on the server, so the
 *  page can rejoin a job it started minutes ago. */
const JOB_KEY = "wise.generation-job";

function slugify(v: string): string {
  return v.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 55);
}

function stepFor(progress: number): number {
  let i = 0;
  STEPS.forEach((s, idx) => {
    if (progress >= s.at) i = idx;
  });
  return i;
}

type Phase = "input" | "generating" | "done" | "error";
/** Build it with AI, or start from a connector someone already published. */
type Source = "ai" | "exchange";

export default function NewConnection() {
  const { currentTenant } = useAuth();
  const [source, setSource] = useState<Source>("ai");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [spec, setSpec] = useState("");
  const [phase, setPhase] = useState<Phase>("input");
  const [job, setJob] = useState<GenerationJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const abort = useRef<AbortController | null>(null);

  const effectiveSlug = slugEdited ? slug : slugify(name);

  /** Follow a job to completion, mirroring each update into the UI. */
  const follow = useCallback(
    async (tenantSlug: string, jobId: string, startedAt: number) => {
      abort.current?.abort();
      const ctrl = new AbortController();
      abort.current = ctrl;
      localStorage.setItem(JOB_KEY, JSON.stringify({ id: jobId, tenant: tenantSlug, startedAt }));
      setPhase("generating");
      try {
        const final = await waitForJob(tenantSlug, jobId, setJob, ctrl.signal);
        localStorage.removeItem(JOB_KEY);
        if (final.status === "succeeded" && final.connection) {
          setJob(final);
          setPhase("done");
        } else {
          setError(final.error || "Generation didn't complete.");
          setJob(final);
          setPhase("error");
        }
      } catch (err) {
        if (ctrl.signal.aborted) return; // superseded or unmounted
        localStorage.removeItem(JOB_KEY);
        setError(err instanceof ApiError ? err.message : "Lost contact with the server.");
        setPhase("error");
      }
    },
    [],
  );

  // Rejoin a generation that was already running when the page loaded.
  useEffect(() => {
    if (!currentTenant) return;
    const raw = localStorage.getItem(JOB_KEY);
    if (!raw) return;
    try {
      const saved = JSON.parse(raw) as { id: string; tenant: string; startedAt: number };
      if (saved.tenant !== currentTenant.slug) return;
      setElapsed(Math.floor((Date.now() - saved.startedAt) / 1000));
      void follow(saved.tenant, saved.id, saved.startedAt);
    } catch {
      localStorage.removeItem(JOB_KEY);
    }
  }, [currentTenant, follow]);

  useEffect(() => () => abort.current?.abort(), []);

  // Elapsed timer — generation takes minutes, so show that time is passing.
  useEffect(() => {
    if (phase !== "generating") return;
    const t = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!currentTenant) return;
    setError(null);
    setJob(null);
    setElapsed(0);
    setPhase("generating");
    try {
      // Per the Connector Generation Standard the backend names the connector
      // after the system codegen identifies; only send overrides the user typed.
      const started = await createFromChat(currentTenant.slug, {
        ...(effectiveSlug ? { slug: effectiveSlug } : {}),
        ...(name ? { display_name: name } : {}),
        spec,
      });
      setJob(started);
      void follow(currentTenant.slug, started.id, Date.now());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't reach the server. Try again.");
      setPhase("error");
    }
  }

  function reset() {
    abort.current?.abort();
    localStorage.removeItem(JOB_KEY);
    setPhase("input");
    setJob(null);
    setError(null);
    setName("");
    setSlug("");
    setSlugEdited(false);
    setSpec("");
  }

  return (
    <>
      <PageHeader
        title="New integration"
        description="Describe the system in plain English. Wise 2.0 drafts the connector, tests it in a sandbox, and registers it."
      />

      {phase === "input" && (
        <>
          {/* Two ways in: describe a new system, or reuse a published connector. */}
          <div className="mb-6 grid gap-3 sm:grid-cols-2">
            <SourceCard
              active={source === "ai"}
              onClick={() => setSource("ai")}
              icon={<Sparkles className="h-5 w-5" />}
              title="Describe it"
              description="Wise 2.0 writes the connector, tests it, and registers it."
            />
            <SourceCard
              active={source === "exchange"}
              onClick={() => setSource("exchange")}
              icon={<Store className="h-5 w-5" />}
              title="Use an existing connector"
              description="Install one from the Wise Exchange and just add credentials."
            />
          </div>

          {source === "exchange" ? (
            <FromExchange tenantSlug={currentTenant?.slug ?? ""} />
          ) : (
        <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
          <Card>
            <CardBody>
              <form onSubmit={onSubmit} className="space-y-5">
                <Field label="What do you want to connect?" hint="Be specific about auth, endpoints, and the capabilities you need.">
                  <Textarea
                    rows={5}
                    required
                    placeholder="e.g. Connect our Salesforce sandbox over OAuth2 client-credentials and add a capability to query recent opportunities…"
                    value={spec}
                    onChange={(e) => setSpec(e.target.value)}
                  />
                </Field>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Try an example</p>
                  <div className="flex flex-wrap gap-2">
                    {EXAMPLES.map((ex) => (
                      <button
                        key={ex}
                        type="button"
                        onClick={() => setSpec(ex)}
                        className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-left text-xs text-slate-500 transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700"
                      >
                        {ex.length > 52 ? ex.slice(0, 52) + "…" : ex}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label="Name (optional)"
                    hint="Leave blank and the connector is named after the system — e.g. Salesforce."
                  >
                    <Input placeholder="Named after the system by default" value={name} onChange={(e) => setName(e.target.value)} />
                  </Field>
                  <Field label="Slug (optional)" hint="Auto: the system's slug, e.g. salesforce.">
                    <Input
                      pattern="[a-z0-9][a-z0-9\-]*"
                      placeholder="auto"
                      value={effectiveSlug}
                      onChange={(e) => {
                        setSlugEdited(true);
                        setSlug(slugify(e.target.value));
                      }}
                    />
                  </Field>
                </div>
                <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-5">
                  <Button type="submit" disabled={!currentTenant} size="lg">
                    <Sparkles className="h-4 w-4" /> Generate connector
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          <aside className="space-y-4">
            <Card className="bg-slate-900 text-white">
              <CardBody>
                <Sparkles className="h-5 w-5 text-accent-300" />
                <h3 className="mt-3 text-sm font-semibold">How it works</h3>
                <ol className="mt-3 space-y-3 text-sm text-slate-300">
                  {STEPS.map((s, i) => (
                    <li key={s.label} className="flex gap-2.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-[11px] font-bold">
                        {i + 1}
                      </span>
                      {s.label}
                    </li>
                  ))}
                </ol>
              </CardBody>
            </Card>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
              <p className="font-semibold text-slate-700">Safe by design</p>
              <p className="mt-1.5">Generated code never runs on the app server — only inside an isolated gVisor sandbox with no network.</p>
            </div>
          </aside>
        </div>
          )}
        </>
      )}

      {phase === "generating" && (
        <GeneratingView job={job} elapsed={elapsed} onCancel={reset} />
      )}

      {phase === "done" && job?.connection && (
        <ResultView job={job} onReset={reset} tenantSlug={currentTenant?.slug ?? ""} />
      )}

      {phase === "error" && (
        <Card className="mx-auto max-w-xl">
          <CardBody className="flex flex-col items-center py-10 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50 text-amber-500">
              <AlertTriangle className="h-7 w-7" />
            </span>
            <h3 className="mt-4 text-lg font-semibold text-slate-900">Couldn't generate the connector</h3>
            <p className="mt-1.5 max-w-md text-sm text-slate-500">{error}</p>
            {job?.test_detail && (
              <pre className="mt-5 max-h-48 w-full overflow-auto rounded-xl bg-slate-900 px-4 py-3 text-left text-xs leading-relaxed text-slate-200">
                {job.test_detail.trim()}
              </pre>
            )}
            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={reset}>Back</Button>
              <Button onClick={() => setPhase("input")}>Try again</Button>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
}

function SourceCard({
  active,
  onClick,
  icon,
  title,
  description,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "focus-ring flex items-start gap-3.5 rounded-2xl border p-4 text-left transition",
        active
          ? "border-brand-400 bg-white shadow-lift ring-1 ring-brand-200"
          : "border-slate-200 bg-white/60 hover:border-slate-300 hover:bg-white",
      )}
    >
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
          active ? "bg-brand-gradient text-white" : "bg-slate-100 text-slate-500",
        )}
      >
        {icon}
      </span>
      <span>
        <span className="block text-sm font-semibold text-slate-900">{title}</span>
        <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{description}</span>
      </span>
    </button>
  );
}

/** Install a published connector into this workspace, then go configure it. */
function FromExchange({ tenantSlug }: { tenantSlug: string }) {
  const navigate = useNavigate();
  const [picked, setPicked] = useState<ExchangeListing | null>(null);
  const [slug, setSlug] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function choose(l: ExchangeListing) {
    setPicked(l);
    setSlug(l.slug);
    setError(null);
  }

  async function install() {
    if (!picked || !tenantSlug) return;
    setBusy(true);
    setError(null);
    try {
      const conn = await installListing(tenantSlug, picked.slug, {
        ...(slug && slug !== picked.slug ? { slug } : {}),
      });
      // Straight to the config form — credentials are all that's missing.
      navigate(`/app/connections/${conn.slug}`, { state: { tenantSlug } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't install this connector.");
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
      <Card>
        <CardBody>
          <ConnectorPicker value={picked} onSelect={choose} />

          {picked && (
            <div className="mt-5 border-t border-slate-100 pt-5">
              {error && (
                <div className="mb-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-100">
                  {error}
                </div>
              )}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <Field
                    label="Name it in this workspace"
                    hint="The slug this connection gets here. Handy when you connect two of the same system."
                  >
                    <Input
                      value={slug}
                      pattern="[a-z0-9][a-z0-9\-]*"
                      onChange={(e) => setSlug(slugify(e.target.value))}
                    />
                  </Field>
                </div>
                <Button size="lg" onClick={install} loading={busy} disabled={!slug}>
                  Install {picked.display_name} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <aside className="space-y-4">
        <Card className="bg-slate-900 text-white">
          <CardBody>
            <BadgeCheck className="h-5 w-5 text-accent-300" />
            <h3 className="mt-3 text-sm font-semibold">The Wise Exchange</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              Connectors we publish and maintain, available to every workspace. Installing
              copies the connector into this workspace — you add your own credentials, and
              nothing is shared with anyone else.
            </p>
          </CardBody>
        </Card>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">
          <p className="font-semibold text-slate-700">Need something else?</p>
          <p className="mt-1.5">
            Nothing in the catalog fits? Switch to <span className="font-medium text-slate-700">Describe it</span> and
            Wise 2.0 will build the connector for you.
          </p>
        </div>
      </aside>
    </div>
  );
}

function mmss(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function GeneratingView({
  job,
  elapsed,
  onCancel,
}: {
  job: GenerationJob | null;
  elapsed: number;
  onCancel: () => void;
}) {
  const progress = job?.progress ?? 0;
  const step = stepFor(progress);
  const running = job && !isJobDone(job);
  return (
    <Card className="mx-auto max-w-xl">
      <CardBody className="py-10">
        <div className="flex flex-col items-center text-center">
          <span className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-lift">
            <Sparkles className="h-7 w-7" />
            <span className="absolute inset-0 animate-ping rounded-2xl bg-brand-400/40" />
          </span>
          <h3 className="mt-5 text-lg font-semibold text-slate-900">
            {job?.phase || "Building your connector…"}
          </h3>
          <p className="mt-1 text-sm text-slate-400">
            Drafting and testing usually takes one to three minutes · {mmss(elapsed)}
          </p>
        </div>

        <div className="mx-auto mt-6 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-brand-gradient transition-[width] duration-700 ease-out"
            style={{ width: `${Math.max(progress, 4)}%` }}
          />
        </div>

        <ol className="mx-auto mt-8 max-w-sm space-y-3">
          {STEPS.map((s, i) => {
            const done = i < step || progress >= 100;
            const active = i === step && !!running;
            return (
              <li key={s.label} className="flex items-center gap-3 text-sm">
                {done ? (
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                ) : active ? (
                  <Loader2 className="h-5 w-5 animate-spin text-brand-500" />
                ) : (
                  <span className="h-5 w-5 rounded-full border-2 border-slate-200" />
                )}
                <span className={cn(done ? "text-slate-500 line-through" : active ? "font-medium text-slate-800" : "text-slate-400")}>
                  {s.label}
                </span>
              </li>
            );
          })}
        </ol>

        <p className="mt-8 text-center text-xs text-slate-400">
          This runs on the server — you can leave this page and come back.
          <button onClick={onCancel} className="ml-2 font-medium text-slate-500 underline hover:text-slate-700">
            Stop watching
          </button>
        </p>
      </CardBody>
    </Card>
  );
}

function ResultView({
  job,
  onReset,
  tenantSlug,
}: {
  job: GenerationJob;
  onReset: () => void;
  tenantSlug: string;
}) {
  const c = job.connection!;
  const passed = c.status === "healthy";
  return (
    <div className="mx-auto max-w-2xl animate-slide-up">
      <Card>
        <CardBody>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-500">
                <Plug className="h-6 w-6" />
              </span>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{c.display_name}</h3>
                <p className="text-sm text-slate-400">{c.system_type}</p>
              </div>
            </div>
            <StatusBadge status={c.status} />
          </div>

          <div
            className={cn(
              "mt-5 flex items-center gap-2.5 rounded-xl px-4 py-3 text-sm",
              passed ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700",
            )}
          >
            {passed ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
            {passed
              ? "Connector generated and its tests passed in the sandbox."
              : c.status === "untested"
                ? "Connector generated and stored. Sandbox testing wasn't available."
                : "Connector generated, but its tests didn't pass — review below."}
          </div>

          {c.capabilities.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Capabilities</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.capabilities.map((cap) => (
                  <code key={cap} className="rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-brand-700">
                    {cap}
                  </code>
                ))}
              </div>
            </div>
          )}

          {job.codegen_notes && (
            <div className="mt-5 rounded-xl border border-slate-100 bg-slate-50/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">AI notes</p>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{job.codegen_notes}</p>
            </div>
          )}

          {job.test_detail && (
            <div className="mt-5 overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5 text-xs font-medium text-slate-300">
                <Terminal className="h-3.5 w-3.5" /> Sandbox output
              </div>
              <pre className="max-h-60 overflow-auto px-4 py-3 text-xs leading-relaxed text-slate-200">
                {job.test_detail.trim()}
              </pre>
            </div>
          )}
        </CardBody>
        <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={onReset}>
            Build another
          </Button>
          <Link
            to={`/app/connections/${c.slug}`}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-brand-gradient px-5 text-sm font-semibold text-white shadow-lift"
            state={{ tenantSlug }}
          >
            Configure connection <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Card>
    </div>
  );
}
